import React, { useEffect, useState } from "react";
import { Mic, Trash2, Play, Pause, GraduationCap, Heart } from "lucide-react";
import { toast } from "sonner";
import VoiceRecorder from "./VoiceRecorder";
import api, { getErrorMessage } from "../lib/api";
import { uploadInChunks } from "../lib/upload";
import { modules } from "../data/mock";
import { useProgress } from "../context/ProgressContext";
import { WifiOff } from "lucide-react";

const audioUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/lesson-intro-audio/${id}?auth=${localStorage.getItem("tycc_token")}`;
const lessonTitle = (mid) => modules.find((m) => m.id === mid)?.title || mid;

export const IntroPlayer = ({ intro, tone = "purple" }) => {
  const ref = React.useRef(null);
  const [playing, setPlaying] = useState(false);
  const c = tone === "purple" ? "bg-[#7d5fb2] hover:bg-[#6a4fa0]" : "bg-[#e5356f] hover:bg-[#c92a5e]";
  return (
    <span className="inline-flex items-center gap-2">
      <audio ref={ref} src={audioUrl(intro.id)} preload="none" onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} />
      <button type="button" onClick={() => (playing ? ref.current.pause() : ref.current.play().catch(() => toast.error("Could not play the pep talk")))} className={`w-9 h-9 rounded-full ${c} text-white flex items-center justify-center flex-shrink-0`} aria-label={playing ? "Pause pep talk" : `Play pep talk from ${intro.author_name}`} data-testid={`intro-play-${intro.id}`}>{playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}</button>
    </span>
  );
};

// Followers (and parents) record a <=20s pep talk that plays when the child opens a chosen lesson.
const PepTalkRecorder = ({ child }) => {
  const { syncStatus: { online } } = useProgress();
  const [moduleId, setModuleId] = useState(modules[0].id);
  const [voice, setVoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [intros, setIntros] = useState([]);
  const [open, setOpen] = useState(false);
  const isServerChild = !!(child?.ownerId || child?.readOnly);

  useEffect(() => {
    if (!isServerChild) return;
    api.get(`/children/${child.id}/intros`).then((r) => setIntros(r.data)).catch(() => setIntros([]));
  }, [child?.id, isServerChild]);

  if (!isServerChild) return null;

  const send = async () => {
    if (!voice) return;
    setBusy(true);
    try {
      setProgress(0);
      const ext = /mp4/.test(voice.mime) ? "m4a" : /ogg/.test(voice.mime) ? "ogg" : "webm";
      const uploadId = await uploadInChunks(new File([voice.blob], `pep-talk.${ext}`, { type: voice.mime }), setProgress);
      const r = await api.post(`/children/${child.id}/lessons/${moduleId}/intros`, { upload_id: uploadId });
      setIntros((l) => [r.data, ...l.filter((i) => !(i.module_id === moduleId && i.mine))]);
      URL.revokeObjectURL(voice.url);
      setVoice(null);
      toast.success("Pep talk saved", { description: `${child.panda.name} will hear it when they open "${lessonTitle(moduleId)}".` });
    } catch (e) { toast.error(getErrorMessage(e, "Could not save the pep talk")); } finally { setBusy(false); setProgress(null); }
  };

  const remove = async (i) => {
    try {
      await api.delete(`/children/${child.id}/lessons/${i.module_id}/intros/${i.id}`);
      setIntros((l) => l.filter((x) => x.id !== i.id));
      toast.success("Pep talk removed");
    } catch (e) { toast.error(getErrorMessage(e, "Could not remove")); }
  };

  return (
    <div className="mt-4 bg-[#f3e8ff] border-2 border-[#d8c4f5] rounded-2xl p-4" data-testid={`pep-talk-${child.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-[#4c1d95] text-sm inline-flex items-center gap-2"><GraduationCap size={16} className="text-[#7d5fb2]" /> Lesson pep talks {intros.length ? `(${intros.length})` : ""}</p>
        <button type="button" onClick={() => setOpen((o) => !o)} className="ml-auto text-xs font-bold text-[#7d5fb2] hover:underline inline-flex items-center gap-1" title={online ? undefined : "Recording needs a connection"} data-testid={`pep-talk-toggle-${child.id}`}><Mic size={12} /> {open ? "Hide" : "Record a pep talk"}</button>
      </div>
      {!online && <p className="mt-2 text-[11px] font-semibold text-[#8a5a00] inline-flex items-center gap-1" data-testid={`pep-talk-offline-${child.id}`}><WifiOff size={11} /> Recording needs a connection - you're offline right now.</p>}
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-[#4c1d95]">Pick a lesson, then record up to 20 seconds. {child.panda.name} hears it the moment that lesson opens.</p>
          <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} className="w-full h-9 rounded-full px-3 text-xs font-bold border-2 border-[#d8c4f5] bg-white text-[#4c1d95] focus:outline-none" data-testid={`pep-talk-lesson-${child.id}`}>
            {modules.map((m, k) => { const label = `Lesson ${k + 1}: ${m.title}`; return <option key={m.id} value={m.id} label={label}>{label}</option>; })}
          </select>
          <div className="flex flex-wrap items-center gap-2">
            <VoiceRecorder value={voice} onChange={setVoice} disabled={busy || !online} title={online ? undefined : "Recording needs a connection"} />
            {voice && <button type="button" onClick={send} disabled={busy || !online} className="btn-pill bg-[#7d5fb2] hover:bg-[#6a4fa0] text-white text-xs px-4 py-1.5 disabled:opacity-50" title={online ? undefined : "Saving needs a connection"} data-testid={`pep-talk-send-${child.id}`}>{progress !== null ? `${progress}%` : "Save pep talk"}</button>}
          </div>
        </div>
      )}
      {intros.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`pep-talk-list-${child.id}`}>
          {intros.map((i) => (
            <li key={i.id} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 text-xs" data-testid={`pep-talk-item-${i.id}`}>
              <IntroPlayer intro={i} />
              <span className="flex-1 min-w-0 text-[#4c1d95]"><span className="font-bold">{i.author_name}</span> · {lessonTitle(i.module_id)}</span>
              {i.hearted && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#e5356f] bg-[#fff0f5] rounded-full px-2 py-0.5" title={`${child.panda.name} tapped the heart`} data-testid={`pep-talk-heard-${i.id}`}><Heart size={11} fill="#e5356f" /> heard by {child.panda.name}</span>}
              {(i.mine || !child.readOnly) && <button type="button" onClick={() => remove(i)} className="text-gray-400 hover:text-[#e5356f]" aria-label="Remove pep talk" data-testid={`pep-talk-delete-${i.id}`}><Trash2 size={13} /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PepTalkRecorder;
